import { monthLabel } from '../model/months'
import type { SeriesPoint } from '../model/portfolioOverview'
import type { MonthKey } from '../model/types'
import { longDate } from '../portfolioText'
import { type ChartGap, chartGaps, MAX_NAMED_AXIS_POINTS } from './chartGaps'

export const CHART_WIDTH = 591
const PLOT_LEFT = 44
const PLOT_RIGHT = 581
export const PLOT_TOP = 8
export const PLOT_BOTTOM = 106
export const X_LABEL_Y = 122
export const GAP_LABEL_Y = 146
const HEIGHT_WITH_GAP_LABELS = 150
const HEIGHT_WITHOUT_GAP_LABELS = 130
const MAX_HATCH_WIDTH = 28
const HATCH_SHARE_OF_STEP = 0.233
const DOMAIN_PADDING = 0.08
const TARGET_TICK_COUNT = 3
// Up to this many points, every point gets its own dated label.
const MAX_DATED_LABELS = MAX_NAMED_AXIS_POINTS

export interface ChartPoint {
  month: MonthKey
  x: number
  // Null when the month has no total: the line breaks and no dot is drawn.
  y: number | null
  isAsOf: boolean
  // True when months with a missing statement sit between this point and the one before.
  breaksBefore: boolean
}

export interface ChartLabel {
  x: number
  text: string
  anchor: 'start' | 'middle' | 'end'
  strong: boolean
}

export interface GapBand {
  key: string
  x: number
  width: number
}

export interface ChartTick {
  y: number
  text: string
}

export interface ChartGeometry {
  height: number
  points: ChartPoint[]
  // Each run of months with a total, as an SVG polyline "x,y x,y".
  lines: string[]
  gaps: GapBand[]
  ticks: ChartTick[]
  xLabels: ChartLabel[]
  gapLabels: ChartLabel[]
  plotLeft: number
  plotRight: number
}

const niceStep = (rough: number): number => {
  const magnitude = 10 ** Math.floor(Math.log10(rough))
  const scaled = rough / magnitude
  if (scaled <= 1) return magnitude
  if (scaled <= 2) return 2 * magnitude
  if (scaled <= 2.5) return 2.5 * magnitude
  if (scaled <= 5) return 5 * magnitude
  return 10 * magnitude
}

const UNITS = [
  { size: 1e9, suffix: 'B' },
  { size: 1e6, suffix: 'M' },
  { size: 1e3, suffix: 'K' },
  { size: 1, suffix: '' },
]

// "$2.9M", "$11M", "-$850K"
export const tickText = (value: number, step: number): string => {
  const unit =
    UNITS.find((candidate) => Math.abs(value) >= candidate.size) ??
    UNITS[UNITS.length - 1]
  const stepInUnits = step / unit.size
  const decimals = stepInUnits >= 1 ? 0 : stepInUnits >= 0.1 ? 1 : 2
  const sign = value < 0 ? '-' : ''
  return `${sign}$${(Math.abs(value) / unit.size).toFixed(decimals)}${unit.suffix}`
}

const valueRange = (totals: number[]) => {
  const low = Math.min(...totals)
  const high = Math.max(...totals)
  if (low !== high) return { low, high }
  const spread = Math.abs(low) * 0.05 || 1
  return { low: low - spread, high: high + spread }
}

const scaleFor = (totals: number[]) => {
  const { low, high } = valueRange(totals)
  const step = niceStep((high - low) / TARGET_TICK_COUNT)
  const tickValues: number[] = []
  const firstIndex = Math.ceil((low - step / 2) / step)
  const lastIndex = Math.floor((high + step / 2) / step)
  for (let index = firstIndex; index <= lastIndex; index++) {
    tickValues.push(index * step)
  }
  const domainLow = Math.min(low, tickValues[0] ?? low)
  const domainHigh = Math.max(high, tickValues[tickValues.length - 1] ?? high)
  const padding = (domainHigh - domainLow) * DOMAIN_PADDING
  const bottom = domainLow - padding
  const top = domainHigh + padding
  const yOf = (value: number) =>
    PLOT_BOTTOM - ((value - bottom) / (top - bottom)) * (PLOT_BOTTOM - PLOT_TOP)
  return { step, tickValues, yOf }
}

const xPositions = (count: number) => {
  const span = PLOT_RIGHT - PLOT_LEFT
  const step = count > 1 ? span / (count - 1) : 0
  return {
    step,
    xOf: (index: number) => (count > 1 ? PLOT_LEFT + index * step : PLOT_RIGHT),
  }
}

const lineRuns = (points: ChartPoint[]): string[] => {
  const runs: string[][] = []
  let current: string[] = []
  const closeRun = () => {
    if (current.length > 1) runs.push(current)
    current = []
  }
  for (const point of points) {
    if (point.breaksBefore) closeRun()
    if (point.y == null) {
      closeRun()
      continue
    }
    current.push(`${point.x},${point.y}`)
  }
  closeRun()
  return runs.map((run) => run.join(' '))
}

const anchorAt = (index: number, count: number): ChartLabel['anchor'] => {
  if (index === 0) return 'start'
  if (index === count - 1) return 'end'
  return 'middle'
}

// About how wide one character of the 11px axis text is.
const CHARACTER_WIDTH = 5.8
const LABEL_SPACING = 12

const extentOf = (label: ChartLabel) => {
  const width = label.text.length * CHARACTER_WIDTH
  if (label.anchor === 'start') return { left: label.x, right: label.x + width }
  if (label.anchor === 'end') return { left: label.x - width, right: label.x }
  return { left: label.x - width / 2, right: label.x + width / 2 }
}

const overlaps = (first: ChartLabel, second: ChartLabel) => {
  const one = extentOf(first)
  const other = extentOf(second)
  return (
    one.left < other.right + LABEL_SPACING &&
    other.left < one.right + LABEL_SPACING
  )
}

// Keep the labels in priority order, dropping any that would overlap one already kept.
const withoutCrowding = (labels: ChartLabel[]) => {
  const kept: ChartLabel[] = []
  for (const label of labels) {
    if (!kept.some((other) => overlaps(other, label))) kept.push(label)
  }
  return kept.sort((first, second) => first.x - second.x)
}

const datedLabels = (
  series: SeriesPoint[],
  asOfMonth: MonthKey,
  xOf: (index: number) => number,
): ChartLabel[] =>
  series.map((point, index) => ({
    x: xOf(index),
    text: longDate(point.asOfDate),
    anchor: anchorAt(index, series.length),
    strong: point.month === asOfMonth,
  }))

// Many points: the first, the last, the as-of month and each December, as "Dec 2023".
const monthLabels = (
  series: SeriesPoint[],
  asOfMonth: MonthKey,
  xOf: (index: number) => number,
): ChartLabel[] => {
  const labelAt = (index: number): ChartLabel => ({
    x: xOf(index),
    text: monthLabel(series[index].month),
    anchor: anchorAt(index, series.length),
    strong: series[index].month === asOfMonth,
  })
  const last = series.length - 1
  const asOfIndex = series.findIndex((point) => point.month === asOfMonth)
  const decembers = series.flatMap((point, index) =>
    point.month.endsWith('-12') ? [index] : [],
  )
  const priority = [asOfIndex, 0, last, ...decembers].filter(
    (index, position, all) => index >= 0 && all.indexOf(index) === position,
  )
  return withoutCrowding(priority.map(labelAt))
}

const gapAnchor = (gap: ChartGap, pointCount: number) =>
  Number.isInteger(gap.position)
    ? anchorAt(gap.position, pointCount)
    : ('middle' as const)

export const buildChartGeometry = (
  series: SeriesPoint[],
  asOfMonth: MonthKey,
): ChartGeometry => {
  const totals = series.flatMap((point) =>
    point.total == null ? [] : [point.total],
  )
  const {
    step: tickStep,
    tickValues,
    yOf,
  } = scaleFor(totals.length > 0 ? totals : [0])
  const { step: xStep, xOf } = xPositions(series.length)
  const gaps = chartGaps(series)
  const breakPositions = new Set(
    gaps.flatMap((gap) =>
      Number.isInteger(gap.position) ? [] : [gap.position + 0.5],
    ),
  )
  const points = series.map((point, index) => ({
    month: point.month,
    x: xOf(index),
    y: point.total == null ? null : yOf(point.total),
    isAsOf: point.month === asOfMonth,
    breaksBefore: breakPositions.has(index),
  }))
  const hatchWidth = Math.min(MAX_HATCH_WIDTH, xStep * HATCH_SHARE_OF_STEP)
  const asOfFirst = [...gaps].sort(
    (first, second) =>
      Number(second.key === asOfMonth) - Number(first.key === asOfMonth),
  )
  const gapLabels = withoutCrowding(
    asOfFirst.map((gap) => ({
      x: xOf(gap.position),
      text: gap.label,
      anchor: gapAnchor(gap, series.length),
      strong: false,
    })),
  )
  return {
    height:
      gapLabels.length > 0 ? HEIGHT_WITH_GAP_LABELS : HEIGHT_WITHOUT_GAP_LABELS,
    points,
    lines: lineRuns(points),
    gaps: gaps.map((gap) => ({
      key: gap.key,
      x: xOf(gap.position) - hatchWidth / 2,
      width: hatchWidth,
    })),
    ticks: tickValues.map((value) => ({
      y: yOf(value),
      text: tickText(value, tickStep),
    })),
    xLabels:
      series.length <= MAX_DATED_LABELS
        ? datedLabels(series, asOfMonth, xOf)
        : monthLabels(series, asOfMonth, xOf),
    gapLabels,
    plotLeft: PLOT_LEFT,
    plotRight: PLOT_RIGHT,
  }
}
